import React, { useEffect, useState } from 'react';
import { getChatUIProviderConfig } from '@/utils/getChatUIProviderConfig';
import { agentStore, initStore, userStore } from '@/stores';
import { getButtonAction } from '@/utils/getButtonData';
import { WebForm as WebFormUI, RNPureConfigProvider } from '@shopee/chatbot-ui/rn';
import { WebFormProps as WebFormUIProps } from '@shopee/chatbot-ui/rn/components/Webform/rn';
import { getCallbackParams, webformNewApiSubmitActionParams } from '@/utils/common';
import { addDialogue } from '@/utils/sessionUtils';
import { next } from '@/utils/next';
import { COUNTRY } from '@/utils/locale';
import * as Toast from '@/utils/Toast';
import { isMultipleWebform, submitWebform } from '@/services/chat';
import uploadMessageToSave from '@/utils/uploadMessageToSave';
import { getTrans } from '@/utils/tools';
import sessionStorage from '@/utils/cache';
import { TRACKING_SECTION, TRACKING_TARGET, TRACKING_TYPE } from '@shopee/chatbot-ui/rn/utils/tracking/constants';
import { TRACKING_MSG_TYPE } from '@/utils/constants';
import { DIALOGUE_TYPE } from '@/types/common';
import { trackingHandler } from '@/utils/btnClickHandler';
import { isBank } from '@/config/core';

export type WebFormProps = Partial<WebFormUIProps> & {
  submitCb?: (btn_id: string) => void;
  listPos: number;
  isHistory?: boolean;
  sessionId?: string;
  dialogueId?: string;
  historySessionId?: string;
  historyDialogueId?: string;
  intent_id?: string;
  onClosePopup?: () => void;
};

const WebForm: React.FC<WebFormProps> = (props) => {
  const { submitCb, listPos, isHistory, onClosePopup, intent_id, ...formProps } = props;
  const [cacheMeta, setCacheMeta] = useState<any>({});
  const [errList, setErrList] = useState<string[]>([]);

  useEffect(() => {
    const cacheParams = sessionStorage.session.getItem(`${listPos}_${props.action?.buttonInfo?.button_id}`);
    if (cacheParams?.formData) {
      setCacheMeta(cacheParams);
    } else {
      setCacheMeta({});
    }
  }, [listPos, props.action?.buttonInfo?.button_id, props.formData, props.mode, props.selectItem]);
  const callback = (status: boolean) => {
    const callbackAction = props?.action?.buttonInfo?.callback_action;
    if (
      !agentStore.inAgent
      && callbackAction
      && getButtonAction(callbackAction.action_type) === 'next'
      && callbackAction?.nextParams
    ) {
      const customCallback = getCallbackParams(props?.action?.info, props?.action?.customCallback);
      next({
        ...{
          ...callbackAction.nextParams,
          message_extra: {
            ...(callbackAction.nextParams?.message_extra || {}),
            task_bot_action: {
              ...webformNewApiSubmitActionParams,
              ...(callbackAction.nextParams?.message_extra?.task_bot_action || {}),
              params: {
                ...(callbackAction.nextParams?.message_extra?.task_bot_action?.params || {}),
                submit_status: { data_type: 4, bool_val: status },
              },
            },
          },
        },
        ...customCallback,
      }).then((data) => {
        callbackAction?.successCb?.(data);

        trackingHandler({
          trackingname: 'webformSubmit',
          trackingdata: {
            ...data?.replies?.[0]?.message_extra?.task_flow_text?.webform_report_data,
            ...data?.replies?.[0]?.message_extra?.task_bot_info,
            create_time: new Date().getTime(),
            dialogue_id: callbackAction?.nextParams?.dialogue_id,
            session_id: initStore.sessionId,
            region: COUNTRY,
            tenant_id: 1,
          },
        });
      });
    }
  };
  const handleClose = (cacheParams: any) => {
    // 执行 action
    callback(false);
    if (cacheParams) {
      sessionStorage.session.setItem(`${listPos}_${props.action?.buttonInfo?.button_id}`, cacheParams);
    }
    props.onClosePopup?.();
  };
  const handleSubmit = (data: any) => {
    const { action, multiple_action, ...restData } = data;
    // 告知 BE 表单提交的动作发生
    multiple_action?.buttonInfo?.multiple_action
      && isMultipleWebform({
        dialogue_id: multiple_action?.info?.dialogueId,
        session_id: initStore.sessionId,
        thread_id: multiple_action?.info?.threadId,
        api_key: 'submit_multiple_form_status',
        query_data: JSON.stringify({
          multiple_conditions: {
            ...(multiple_action?.taskbotInfo || {}),
            ...((multiple_action as any)?.task_bot_action?.params || {}),
          },
        }),
      });
    const bankQueryDataParams = isBank() ? { user_id: userStore?.userId } : {};
    submitWebform({
      dialogue_id: action?.info?.dialogueId,
      session_id: action?.info?.sessionId,
      thread_id: action?.info?.threadId,
      message_id: action?.info?.messageId,
      query_data: JSON.stringify({
        form_sn: (data as any).form_sn,
        data: JSON.stringify({ ...(data as any).formData }),
        channel: `CHATBOT_${COUNTRY.toLocaleUpperCase()}`,
        ...bankQueryDataParams,
      }),
    })
      .then((data) => {
        const formRes = JSON.parse(data.orders || '{}');
        const status = +formRes?.code === 200 && !!formRes?.result?.submission_id && !!formRes?.result?.cs_user_id; // 是否提交成功
        callback(status);
        if (status) {
          // TODO: 提交成功需要把 button 禁用
          submitCb?.(action?.buttonInfo?.button_id);
          setErrList([]);
          addDialogue({
            dialogueId: action?.info?.dialogueId,
            dialogueType: action?.info?.dialogueType,
            from: 'user',
            type: DIALOGUE_TYPE.WebFormPreview,
            data: { ...restData, status },
          });
          uploadMessageToSave({
            dialogueId: action?.info?.dialogueId,
            messages: [
              {
                message_text: 'Webform submitted successfully',
                is_sent_by_user: true,
              },
            ],
          });
          props.onClosePopup?.();
          return;
        } else {
          // 提交失败
          const reg = /invalidField=\[\w+\]/g;
          const invalidField = formRes?.msg
            ?.match(reg)?.[0]
            ?.replace(/invalidField=\[/, '')
            ?.replace(/\]/, '');

          if (invalidField) {
            // 如果能够解析出 invalidField 字段, 就交给表单提示错误信息
            // console.log('formRes ===', formRes, invalidField);
            Toast.error(getTrans('error_init_session_tip'));
          } else {
            // 解析不出来任何 invalidField 字段，就 toast 提示
            Toast.error(getTrans('error_init_session_tip'));
          }
        }
      })
      .catch((err) => {
        Toast.error(getTrans('error_init_session_tip'));
        callback(false);
      });
  };
  const trackingData = {
    trackingsection: TRACKING_SECTION[TRACKING_TYPE.WEBFORM],
    trackingtarget: TRACKING_TARGET[TRACKING_TYPE.WEBFORM],
    trackingdataBasic: {
      chat_session_id: isHistory ? props.historySessionId : props.sessionId || '',
      chat_dialogue_id: isHistory ? props.historyDialogueId : props.dialogueId,
      bot_id: props.action?.taskbotInfo?.bot_id,
      intent_id: intent_id,
      if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
      form_title: props.title,
    },
  };

  return (
    <RNPureConfigProvider {...getChatUIProviderConfig()}>
      {formProps.active && (
        <WebFormUI
          {...(formProps as any)}
          formData={cacheMeta.formData || props.formData}
          selectItem={cacheMeta.selectItem}
          mode={cacheMeta.curMode !== undefined ? cacheMeta.curMode : formProps.mode}
          errList={errList}
          onClose={handleClose}
          region={COUNTRY}
          onSubmit={handleSubmit}
          trackingData={trackingData}
        />
      )}
    </RNPureConfigProvider>
  );
};

export default WebForm;
